// Stok kartı "Ambalaj ve barkodlar" taslağı: saf kurallar (sunucudaki PackagingRules ile aynı).
import { parseTrQuantity } from '../../../../../utils/numberFormat';

export const MAX_PACKAGING_ROWS = 20;
export const MAX_EXTRA_BARCODES = 50;
export const MAX_BARCODE_LENGTH = 100;
export const MAX_FACTOR = 1_000_000;

export interface PackagingDraftRow {
  key: string;
  id?: string;
  unitName: string;
  /** İçerik miktarı metni ("4", "10,5"). */
  containsText: string;
  /** İçerikteki birim; '' = ana birim. */
  containsUnit: string;
  /** Ambalajın kendi satış fiyatı; '' = içerik × adet fiyatı. */
  salePriceText: string;
  isDefaultForProduction: boolean;
  recipeUsageCount: number;
  barcodes: string[];
  invalid?: boolean;
}

export interface PackagingDraft {
  rows: PackagingDraftRow[];
  /** Ana birimin ek barkodları (eski/tedarikçi barkodu). */
  baseBarcodes: string[];
}

export const fold = (value?: string | null): string => (value ?? '').trim().toLocaleLowerCase('tr-TR');
export const sameName = (a?: string | null, b?: string | null): boolean => {
  const x = fold(a);
  return x.length > 0 && x === fold(b);
};
export const sameBarcode = (a?: string | null, b?: string | null): boolean => {
  const x = (a ?? '').trim().toLowerCase();
  return x.length > 0 && x === (b ?? '').trim().toLowerCase();
};

const round = (value: number, digits: number): number => {
  const scale = 10 ** digits;
  return Math.round(value * scale) / scale;
};

/** İçerikteki birim ana birimse ya da boşsa ''. */
export function innerUnitOf(row: PackagingDraftRow, baseUnit: string): string {
  const name = row.containsUnit.trim();
  return !name || sameName(name, baseUnit) ? '' : name;
}

export function isValidBarcodeText(code: string): boolean {
  return code.length > 0 && code.length <= MAX_BARCODE_LENGTH && !/[\s\p{Cc}]/u.test(code);
}

/**
 * Her ambalajın ana birim karşılığı (anahtar: katlanmış ad). Çözülemeyen satır (geçersiz içerik,
 * tanımsız iç birim, döngü) haritada yer almaz.
 */
export function computeFactors(rows: PackagingDraftRow[], baseUnit: string): Map<string, number> {
  const byKey = new Map<string, PackagingDraftRow>();
  rows.forEach((row) => {
    const key = fold(row.unitName);
    if (key && !byKey.has(key)) byKey.set(key, row);
  });
  const result = new Map<string, number>();
  const visit = (key: string, path: string[]): number | null => {
    const known = result.get(key);
    if (known !== undefined) return known;
    const row = byKey.get(key);
    if (!row) return null;
    const qty = parseTrQuantity(row.containsText);
    if (!Number.isFinite(qty) || qty <= 0) return null;
    const inner = innerUnitOf(row, baseUnit);
    let innerFactor = 1;
    if (inner) {
      const innerKey = fold(inner);
      if (!byKey.has(innerKey) || path.includes(innerKey)) return null;
      const nested = visit(innerKey, [...path, innerKey]);
      if (nested == null) return null;
      innerFactor = nested;
    }
    const factor = round(qty * innerFactor, 6);
    result.set(key, factor);
    return factor;
  };
  byKey.forEach((_, key) => { visit(key, [key]); });
  return result;
}

/** Ambalajın satış fiyatı: kendi fiyatı ya da çarpan × ana birim fiyatı. */
export function effectivePrice(row: PackagingDraftRow, factor: number | undefined, itemSalePrice: number): number | null {
  const own = parseTrQuantity(row.salePriceText, 4);
  if (row.salePriceText.trim() && Number.isFinite(own)) return own;
  if (factor == null || !Number.isFinite(itemSalePrice)) return null;
  return round(itemSalePrice * factor, 4);
}

export interface DraftValidation {
  message: string | null;
  invalidKeys: Set<string>;
}

/** Kayıt öncesi doğrulama; her kural en fazla bir mesaj üretir. */
export function validateDraft(draft: PackagingDraft, baseUnit: string, mainBarcode: string): DraftValidation {
  const errors: string[] = [];
  const invalid = new Set<string>();
  const { rows } = draft;
  const add = (msg: string) => { if (!errors.includes(msg)) errors.push(msg); };

  if (rows.length > MAX_PACKAGING_ROWS) add(`En fazla ${MAX_PACKAGING_ROWS} ambalaj tanımlanabilir.`);

  const names = new Set<string>();
  rows.forEach((row) => {
    const name = row.unitName.trim();
    if (!name) { add('Ambalaj adı boş olamaz.'); invalid.add(row.key); return; }
    if (name.length > 20) { add('Ambalaj adı en fazla 20 karakter olabilir.'); invalid.add(row.key); }
    if (sameName(name, baseUnit)) { add('Ambalaj adı ana birimle aynı olamaz.'); invalid.add(row.key); }
    if (names.has(fold(name))) { add('Aynı ambalaj adı birden fazla kez kullanılamaz.'); invalid.add(row.key); }
    names.add(fold(name));
  });

  const factors = computeFactors(rows, baseUnit);
  rows.forEach((row) => {
    if (!row.unitName.trim()) return;
    const qty = parseTrQuantity(row.containsText);
    if (!Number.isFinite(qty) || qty <= 0) {
      add('İçerik miktarı sıfırdan büyük olmalıdır.');
      invalid.add(row.key);
      return;
    }
    const factor = factors.get(fold(row.unitName));
    if (factor == null) {
      const inner = innerUnitOf(row, baseUnit);
      add(inner && !names.has(fold(inner))
        ? `'${row.unitName.trim()}' içindeki '${inner}' ambalajı tanımlı değil.`
        : 'Ambalaj içerikleri döngü oluşturuyor (ör. koli içinde paket, paket içinde koli).');
      invalid.add(row.key);
    } else if (factor > MAX_FACTOR) {
      add('Bir ambalaj ana birimde 1.000.000 değerini aşamaz.');
      invalid.add(row.key);
    }
    if (row.salePriceText.trim()) {
      const price = parseTrQuantity(row.salePriceText, 4);
      if (!Number.isFinite(price) || price < 0) {
        add('Ambalaj satış fiyatı geçersiz.');
        invalid.add(row.key);
      }
    }
  });

  if (rows.filter((row) => row.isDefaultForProduction).length > 1) {
    add('En fazla bir varsayılan üretim birimi seçilebilir.');
    rows.forEach((row) => { if (row.isDefaultForProduction) invalid.add(row.key); });
  }

  const all: { code: string; key: string | null }[] = [
    ...draft.baseBarcodes.map((code) => ({ code, key: null })),
    ...rows.flatMap((row) => row.barcodes.map((code) => ({ code, key: row.key }))),
  ];
  if (all.length > MAX_EXTRA_BARCODES) add(`Bir kartta en fazla ${MAX_EXTRA_BARCODES} ek barkod tanımlanabilir.`);
  const seen = new Set<string>();
  all.forEach(({ code, key }) => {
    const flag = () => { if (key) invalid.add(key); };
    if (!isValidBarcodeText(code)) { add(`Geçersiz barkod: '${code}'.`); flag(); return; }
    const lower = code.toLowerCase();
    if (seen.has(lower)) { add(`'${code}' barkodu birden fazla kez girildi.`); flag(); }
    seen.add(lower);
    if (sameBarcode(code, mainBarcode)) { add(`'${code}' zaten kartın ana barkodu.`); flag(); }
  });

  return { message: errors.length > 0 ? errors.join(' ') : null, invalidKeys: invalid };
}

/** Değişiklik tespiti için kararlı parmak izi. */
export function fingerprint(draft: PackagingDraft): string {
  return JSON.stringify({
    rows: draft.rows.map((row) => ({
      id: row.id ?? null,
      n: row.unitName.trim(),
      q: row.containsText.trim(),
      u: row.containsUnit.trim(),
      p: row.salePriceText.trim(),
      d: row.isDefaultForProduction,
      b: row.barcodes,
    })),
    base: draft.baseBarcodes,
  });
}

/** Taslakta hiç ambalaj ve ek barkod yoksa true. */
export function isEmptyDraft(draft: PackagingDraft): boolean {
  return draft.rows.length === 0 && draft.baseBarcodes.length === 0;
}

/** Tüm ek ve ambalaj barkodları (çakışma ön kontrolü için). */
export function allBarcodes(draft: PackagingDraft): string[] {
  return [...draft.baseBarcodes, ...draft.rows.flatMap((row) => row.barcodes)];
}

/** Sunucu kayıt gövdesi (PascalCase). Çarpan içerikten hesaplanır; sunucu da yeniden hesaplar. */
export function toSavePayload(draft: PackagingDraft, baseUnit: string) {
  const factors = computeFactors(draft.rows, baseUnit);
  return {
    Units: draft.rows.map((row, index) => {
      const price = parseTrQuantity(row.salePriceText, 4);
      return {
        Id: row.id ?? null,
        UnitName: row.unitName.trim(),
        Factor: factors.get(fold(row.unitName)) ?? 0,
        ContainsQuantity: parseTrQuantity(row.containsText),
        ContainsUnitName: innerUnitOf(row, baseUnit) || null,
        SalePrice: row.salePriceText.trim() && Number.isFinite(price) ? price : null,
        IsDefaultForProduction: row.isDefaultForProduction,
        SortOrder: index,
      };
    }),
    Barcodes: [
      ...draft.baseBarcodes.map((code) => ({ Barcode: code, UnitName: null })),
      ...draft.rows.flatMap((row) => row.barcodes.map((code) => ({ Barcode: code, UnitName: row.unitName.trim() }))),
    ],
  };
}

/** Sayı gösterimi (tr-TR, en fazla 6 hane). */
export function formatQty(value: number): string {
  return value.toLocaleString('tr-TR', { maximumFractionDigits: 6 });
}
